import type { SetStateAction } from 'react';
import { memo, useCallback } from 'react';
import UnhandledError from '../../core/components/UnhandledError';
import useVehicles from '../queries/vehicles/useVehicles';
import type { MotorcycleSearchFiltersValue } from './MotorcycleSearch/MotorcycleSearchFilters';
import MotorcycleSearchFilters from './MotorcycleSearch/MotorcycleSearchFilters';
import MotorcycleSearchTable from './MotorcycleSearchTable';
import VehicleSearch from './VehicleSearch';

export type MotorcycleSearchParams = MotorcycleSearchFiltersValue & {
    page: number;
    pageSize: number;
};

const MotorcycleSearch = ({
    title = 'motorcycle search',
    params,
    updateParams,
    onModelChange,
    pageSize = 20,
    shortSearch,
}: {
    params: MotorcycleSearchParams;
    updateParams: (params: Partial<MotorcycleSearchParams>) => void;
    onModelChange: (vehicleId: string) => void;
    pageSize?: number;
    title?: string;
    shortSearch?: boolean;
}) => {
    const { page, ...vehicleParams } = params;

    const {
        vehicles,
        total: totalVehiclesCount,
        loading,
        error,
    } = useVehicles({
        ...params,
        page,
        pageSize,
    });

    const setPage = (newPage: number) => {
        updateParams({
            ...params,
            page: newPage,
        });
    };

    const setVehicleParams = useCallback(
        (action: SetStateAction<MotorcycleSearchFiltersValue>) => {
            updateParams({
                ...action,
                page: 1,
            });
        },
        [updateParams]
    );

    return (
        <VehicleSearch
            title={title}
            totalVehiclesCount={totalVehiclesCount}
            vehicleFilters={
                <MotorcycleSearchFilters
                    filters={vehicleParams}
                    onChangeFilters={setVehicleParams}
                    shortSearch={shortSearch}
                />
            }
            vehiclesTable={
                error ? (
                    <UnhandledError>{error}</UnhandledError>
                ) : (
                    <MotorcycleSearchTable
                        onModelChange={onModelChange}
                        motorcycles={vehicles}
                    />
                )
            }
            loading={loading}
            filteredVehicles={vehicles}
            pageSize={pageSize}
            page={params.page}
            setPage={setPage}
            showTopPagination={false}
        />
    );
};

export default memo(MotorcycleSearch);
